import { NextRouter } from "next/router"
import { TOAST_WITH_ACTION_DURATION } from "@/constants/ui"
import {
  createFlightItineraryItem,
  createPlaceItem,
  FlightItineraryItem,
  isPlaceBasedItem,
  TripAddableItem,
  UnknownEntityItem,
  WebSearchResultItem,
} from "@/types/add-to-trip"
import { Timestamp } from "@/types/date"
import { PlaceRef } from "@/types/entity"
import { FolioDocumentRef } from "@/types/folio/folio-document"
import { ItineraryEvent, ItineraryEventCreation } from "@/types/itinerary"
import { Place } from "@/types/place"
import { Trip, TripRef, TripSummary, TripTab } from "@/types/trip"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { cn } from "cn"

import { buildClientAxios } from "@/lib/client-axios"
import { FlightApi } from "@/lib/flight-api"
import { links } from "@/lib/routes"
import { toastManager } from "@/lib/toast"
import { assert, assertUnreachable } from "@/lib/utils/assertions"
import { isChatPage } from "@/lib/utils/page"
import { isExplorePage } from "@/lib/utils/routing"
import { AddToTripTrackingContext } from "@/components/add-to-trip-button"
import { AirlinesImg } from "@/components/airlines-img"
import { EntityIcon } from "@/components/entity-icon"
import { NamedIcon } from "@/components/icons/named-icon"
import { Link } from "@/components/ui/link"
import { Photo } from "@/components/ui/photo"
import { itineraryEventToAmplitudeProperties } from "../analytics/amplitude-properties"
import { sendAnalyticsEvent } from "../analytics/amplitude-utils"
import { addHours } from "../date-utils"
import { getSearchResultIconName, isEvent } from "../entity-utils"
import { formatFlightTitle } from "../flight-utils"
import { formatEventTitle } from "../folio-document-utils"
import {
  defaultTimestampsForPlace,
  eventColorPair,
  folioRef,
  isEventWithMindtripFlightBooking,
  isFolioItineraryEvent,
} from "../itinerary-utils"
import { dayRangeForDraftDates } from "../time-frame-utils"
import { showGenericErrorToast } from "../toast-utils"
import { getTripTravelersOrDefault } from "../trip-utils"
import { prepareWebSearchResultForTrip } from "../web-search-result-utils"
import { chatsApi } from "./api/chats-api"
import { itineraryApi } from "./api/itinerary-api"
import { populateTripCache, tripsApi } from "./api/trips-api"
import { selectEmbedded } from "./app-slice"
import { selectCurrentCollectionRef } from "./collection-slice"
import {
  cancelPendingCloseCleanup,
  openTrip,
  selectTripInSheetStack,
  selectTripPageActive,
  snapshotSheetScroll,
} from "./detail-sheet-slice"
import {
  closeAddToMediaDialog,
  closeAddToTripDialog,
  openAddToTripDialog,
} from "./dialog-slice"
import {
  selectIsEmbeddedChatView,
  selectIsEmbeddedExploreView,
} from "./embedded-slice"
import { selectCurrInspirationRef } from "./inspiration-slice"
import { removeEventsWithPlace } from "./itinerary-remove-events-thunks"
import {
  selectAllPersistentEvents,
  selectEventsWithPlaces,
} from "./itinerary-slice"
import { createEvent } from "./itinerary-thunks"
import { syncPlaceSearchParamsFromTrip } from "./place-thunks"
import { AppDispatch, RootState } from "./redux-store"
import { initTrip } from "./trip-init-thunks"
import tripSlice, {
  removeTrip,
  selectTrip,
  selectTripRef,
  selectTripTravelersOrDefault,
  setLastUsedTripRef,
  setLoadingTripRef,
} from "./trip-slice"
import { selectHideCreateTrip } from "./ui-settings-slice"

export { initTrip }

export const highlightTripPlaceEvent = createAsyncThunk<
  void,
  PlaceRef,
  { dispatch: AppDispatch; state: RootState }
>(
  "tripThunks/highlightTripPlaceEvent",
  async (placeRef, { dispatch, getState }) => {
    const events = selectEventsWithPlaces(getState())
    const event = events.find((e) => e.place?.entity_ref === placeRef)

    if (event) {
      void dispatch(openTripSheet({ eventId: event.id }))
    }
  }
)

export const highlightTripFlightEvent = createAsyncThunk<
  void,
  number,
  { dispatch: AppDispatch; state: RootState }
>(
  "tripThunks/highlightTripFlightEvent",
  async (flightItineraryId, { dispatch, getState }) => {
    const events = selectAllPersistentEvents(getState())
    const event = events.find(
      (e) =>
        isEventWithMindtripFlightBooking(e) &&
        e.flight_itinerary?.id === flightItineraryId
    )

    if (event) {
      void dispatch(openTripSheet({ eventId: event.id }))
    }
  }
)

export const openTripSheet = createAsyncThunk<
  void,
  | {
      tripRef?: TripRef
      trip?: Trip
      place?: Place
      eventId?: number
      folioDocumentRef?: FolioDocumentRef
      tab?: TripTab
    }
  | undefined,
  { dispatch: AppDispatch; state: RootState }
>("tripThunks/openTripSheet", async (params, { dispatch, getState }) => {
  const { tripRef, trip, place, folioDocumentRef, tab } = params ?? {}

  let eventId = params?.eventId

  const tripPageActive = selectTripPageActive(getState())
  const tripWasInSheet = selectTripInSheetStack(getState())
  const currentTripRef = selectTripRef(getState())
  const tripOrRef = trip ?? tripRef
  const requestedTripRef =
    typeof tripOrRef === "string" ? tripOrRef : tripOrRef?.ref
  const targetTripRef = requestedTripRef ?? currentTripRef
  if (!targetTripRef) return

  if (!tripPageActive) {
    void dispatch(closeAddToTripDialog())
    void dispatch(closeAddToMediaDialog())
  }

  cancelPendingCloseCleanup()
  snapshotSheetScroll(dispatch)

  // Start init before pushing the page so it mounts already pointed at the
  // requested trip. Re-initializing the same trip would toggle loadingTripRef
  // and unmount the sheet content, resetting scroll before we reach the event.
  const tripInit =
    tripOrRef && requestedTripRef !== currentTripRef
      ? dispatch(initTrip(tripOrRef))
      : undefined

  dispatch(openTrip({ tripRef: targetTripRef }))

  if (tab) {
    dispatch(tripSlice.actions.setCurrentTab(tab))
  }

  await tripInit

  // When scrolling to an event by place or eventId, ensure the itinerary is
  // loaded first. initTrip fire-and-forgets the itinerary fetch, so the data
  // may not be available yet when we try to look up the event or when the
  // trip-itinerary-item components try to scroll to the highlighted event.
  const resolvedTripRef = selectTripRef(getState())
  if ((place || eventId || folioDocumentRef) && resolvedTripRef) {
    const itineraryState = itineraryApi.endpoints.getItinerary.select({
      tripRef: resolvedTripRef,
    })(getState())
    if (!itineraryState?.data) {
      await dispatch(
        itineraryApi.endpoints.getItinerary.initiate({
          tripRef: resolvedTripRef,
        })
      ).unwrap()
    }
  }

  if (place) {
    const events = selectEventsWithPlaces(getState())
    const event = events.find((e) => e.place?.entity_ref === place.entity_ref)

    if (event) {
      eventId = event.id
    }
  }

  if (!eventId && folioDocumentRef) {
    const events = selectAllPersistentEvents(getState())
    const event = events.find(
      (e) =>
        isFolioItineraryEvent(e) &&
        e.status === "booked" &&
        folioRef(e) === folioDocumentRef
    )
    if (event) {
      eventId = event.id
    }
  }

  if (eventId) {
    dispatch(tripSlice.actions.setHighlightedEventId(eventId))
  }

  // MIN-5945: Add missing trip slideout analytics event
  // Special handling for embedded mode.  The reason is that we handle this in the client-route-hander.tsx but
  // in the embedded mode, the route did not changed. Same trigger as the route
  // handler: a trip entering the sheet stack, not returning to it from a page on top.
  if (selectEmbedded(getState()) && !tripWasInSheet) {
    sendAnalyticsEvent("ViewTripSlideOut")
  }
})

export const handleLeaveTrip = createAsyncThunk<
  void,
  TripRef,
  { dispatch: AppDispatch; state: RootState }
>("tripThunks/handleLeaveTrip", async (tripRef, { dispatch }) => {
  dispatch(setLoadingTripRef(tripRef))

  try {
    await dispatch(tripsApi.endpoints.leaveTrip.initiate(tripRef)).unwrap()
    dispatch(chatsApi.util.invalidateTags(["Chats"]))
    dispatch(
      tripsApi.util.invalidateTags(["NavTrips", "Trips", "PlaceTripRefs"])
    )
    dispatch(removeTrip(tripRef))
  } catch (e) {
    console.error("Failed to leave trip", e)
    toastManager.add({
      type: "error",
      description: "Sorry, something went wrong. Please try again.",
    })
  } finally {
    dispatch(setLoadingTripRef(undefined))
  }
})

export const addPlaceToTrip = createAsyncThunk<
  void,
  {
    place: Place
    trip?: TripSummary
    allowChange?: boolean
    router: NextRouter
    trackingContext: AddToTripTrackingContext
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "tripThunks/addPlaceToTrip",
  async (
    { place, trip, allowChange, router, trackingContext },
    { dispatch, getState }
  ) => {
    const state = getState()
    const chatTrip = selectTrip(state)
    const tripInSheet = selectTripInSheetStack(state)
    const currInspirationRef = selectCurrInspirationRef(state)
    const currCollectionRef = selectCurrentCollectionRef(state)

    const tripToUse = trip || chatTrip

    assert(tripToUse, "trip is required")

    try {
      const eventScheduleData = isEvent(place)
        ? place.schedules?.data?.[0]
        : undefined
      const eventStartAt = eventScheduleData?.start_at
      const eventEndAt = eventScheduleData?.end_at

      const tripDates = dayRangeForDraftDates(
        tripToUse.draft_start_date,
        tripToUse.draft_duration
      )

      let finalStartTimestamp: Timestamp
      let finalEndTimestamp: Timestamp

      if (eventStartAt && eventEndAt) {
        finalStartTimestamp = eventStartAt
        finalEndTimestamp = eventEndAt
      } else if (eventStartAt) {
        finalStartTimestamp = eventStartAt
        finalEndTimestamp = addHours(eventStartAt, 1)
      } else {
        const { startTimestamp, endTimestamp } = defaultTimestampsForPlace(
          place,
          tripDates
        )
        finalStartTimestamp = startTimestamp
        finalEndTimestamp = endTimestamp || startTimestamp
      }

      const eventParams: ItineraryEventCreation = {
        entity_ref: place.entity_ref,
        start_at: finalStartTimestamp,
        end_at: finalEndTimestamp,
        travelers: trip
          ? getTripTravelersOrDefault(trip)
          : selectTripTravelersOrDefault(state),
        commission_source_ref: currInspirationRef || currCollectionRef,
        scheduled: false,
        src_ref: currCollectionRef || currInspirationRef, // Need to send in collection first, then inspiration.  We want to know where this place is added from not where the ultimate commissionable source is from
      }

      const response = await dispatch(
        createEvent({ params: eventParams, tripRef: tripToUse.ref })
      ).unwrap()
      if (response.event) {
        void dispatch(
          syncPlaceSearchParamsFromTrip({
            trip: tripToUse,
            place,
          })
        )
        dispatch(
          tripsApi.util.invalidateTags([
            { type: "PlaceTripRefs", id: place.entity_ref },
          ])
        )
        dispatch(setLastUsedTripRef(tripToUse.ref))
        void dispatch(
          showAddToTripToast({
            router,
            item: createPlaceItem(place),
            trip: tripToUse,
            eventId: response.event.id,
            // we could allow but will require reworking trip dialog
            allowChange,
          })
        )
        if (tripInSheet && chatTrip?.ref === tripToUse.ref) {
          void dispatch(openTripSheet({ eventId: response.event.id }))
        }

        sendAnalyticsEvent({
          name: "ItineraryAdd",
          properties: {
            ...itineraryEventToAmplitudeProperties(response.event),
            context: trackingContext,
          },
        })
      }
    } catch (error) {
      console.error("Failed to add place to trip", error)
      showGenericErrorToast()
    }
  }
)

export const addWebSearchResultToTrip = createAsyncThunk<
  void,
  {
    item: WebSearchResultItem | UnknownEntityItem
    trip?: TripSummary
    router: NextRouter
    trackingContext: AddToTripTrackingContext
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "tripThunks/addWebSearchResultToTrip",
  async ({ item, trip, router, trackingContext }, { dispatch, getState }) => {
    const state = getState()
    const tripToUse = trip || selectTrip(state)
    const chatTripRef = selectTripRef(state)
    const tripInSheet = selectTripInSheetStack(state)
    const currInspirationRef = selectCurrInspirationRef(state)
    const currCollectionRef = selectCurrentCollectionRef(state)

    assert(tripToUse, "trip is required")

    try {
      const displayText = item.title
      const searchResult =
        "searchResult" in item ? item.searchResult : undefined

      const preparedParams = prepareWebSearchResultForTrip(state, {
        title: displayText,
        searchResult,
        tripStartDate: tripToUse.draft_start_date,
        tripTimeZone: tripToUse.time_zone,
        messageId: item.messageId,
        webSearchResultId:
          "webSearchResultId" in item ? item.webSearchResultId : undefined,
      })

      const eventParams: ItineraryEventCreation = {
        title: preparedParams.title,
        type: preparedParams.type,
        start_at: preparedParams.start,
        end_at: preparedParams.end,
        time_zone: preparedParams.timeZone,
        travelers: getTripTravelersOrDefault(tripToUse),
        scheduled: false,
        message_id: preparedParams.messageId,
        web_search_result_id: preparedParams.webSearchResultId,
        entity_ref: preparedParams.entityRef,
        properties: preparedParams.properties,
        commission_source_ref: currInspirationRef || currCollectionRef,
        src_ref: currCollectionRef || currInspirationRef,
      }

      const response = await dispatch(
        createEvent({ params: eventParams, tripRef: tripToUse.ref })
      ).unwrap()

      if (response.event) {
        dispatch(setLastUsedTripRef(tripToUse.ref))
        void dispatch(
          showAddToTripToast({
            router,
            item,
            trip: tripToUse,
            eventId: response.event.id,
          })
        )
        if (tripInSheet && chatTripRef === tripToUse.ref) {
          void dispatch(openTripSheet({ eventId: response.event.id }))
        }

        sendAnalyticsEvent({
          name: "ItineraryAdd",
          properties: {
            ...itineraryEventToAmplitudeProperties(response.event),
            context: trackingContext,
          },
        })
      }
    } catch (error) {
      console.error("Failed to add web search result to trip", error)
      showGenericErrorToast()
    }
  }
)

export const addFlightToTrip = createAsyncThunk<
  void,
  {
    flightItineraryId: number
    airlines?: Array<{ name: string; iata: string; logo_url?: string | null }>
    trip?: TripSummary
    router?: NextRouter
    trackingContext: AddToTripTrackingContext
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "tripThunks/addFlightToTrip",
  async (
    { flightItineraryId, airlines, trip, router },
    { dispatch, getState }
  ) => {
    try {
      const tripToUse = trip || selectTrip(getState())
      assert(tripToUse, "trip is required")
      const flightApi = new FlightApi(buildClientAxios())
      const res = await flightApi.addFlightToTrip(
        tripToUse.ref,
        flightItineraryId
      )
      void populateTripCache(dispatch, res.trip)
      dispatch(
        itineraryApi.util.updateQueryData(
          "getItinerary",
          { tripRef: res.trip.ref },
          () => res.itinerary
        )
      )

      const flightEvent = res.itinerary.events.find(
        (e) =>
          isEventWithMindtripFlightBooking(e) &&
          e.flight_itinerary?.id === flightItineraryId
      )
      const displayName =
        (flightEvent && formatFlightTitle(flightEvent)) || undefined

      void dispatch(
        showAddToTripToast({
          item: createFlightItineraryItem({
            flightItineraryIds: [flightItineraryId],
            airlines,
          }),
          trip: res.trip,
          eventId: flightEvent?.id,
          allowChange: true,
          router,
          displayName,
        })
      )
    } catch (error) {
      console.error("Failed to add flight to trip", error)
      showGenericErrorToast()
    }
  }
)

export const addFlightsToTrip = createAsyncThunk<
  void,
  {
    flightItineraryIds: number[]
    airlines?: Array<{ name: string; iata: string; logo_url?: string | null }>
    trip?: TripSummary
    router?: NextRouter
    trackingContext: AddToTripTrackingContext
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "tripThunks/addFlightsToTrip",
  async (
    { flightItineraryIds, airlines, trip, router },
    { dispatch, getState }
  ) => {
    try {
      const tripToUse = trip || selectTrip(getState())
      assert(tripToUse, "trip is required")
      const flightApi = new FlightApi(buildClientAxios())
      const res = await flightApi.addFlightsToTrip(
        tripToUse.ref,
        flightItineraryIds
      )
      void populateTripCache(dispatch, res.trip)
      dispatch(
        itineraryApi.util.updateQueryData(
          "getItinerary",
          { tripRef: res.trip.ref },
          () => res.itinerary
        )
      )

      const flightEvent = res.itinerary.events.find(
        (e) =>
          isEventWithMindtripFlightBooking(e) &&
          e.flight_itinerary?.id === flightItineraryIds[0]
      )
      const displayName =
        (flightEvent && formatFlightTitle(flightEvent)) || undefined

      void dispatch(
        showAddToTripToast({
          item: createFlightItineraryItem({
            flightItineraryIds,
            airlines,
          }),
          trip: res.trip,
          eventId: flightEvent?.id,
          allowChange: true,
          router,
          displayName,
        })
      )
    } catch (error) {
      console.error("Failed to add flights to trip", error)
      showGenericErrorToast()
    }
  }
)

/** The item's name in the Save to trip dialog and the toast after adding it. */
export const addToTripItemDisplayName = (item: TripAddableItem) => {
  switch (item.kind) {
    case "place":
    case "bookedExternalPlace":
      return item.place.name
    case "folio": {
      const { events } = item.folioTypeGroup
      return events[0]
        ? formatEventTitle(events[0], events.length > 1, events)
        : item.folioTypeGroup.document_type
    }
    case "flightItinerary":
      return "Flight"
    case "webSearchResult":
    case "unknownEntity":
      return item.title
    default:
      return assertUnreachable(item)
  }
}

function addToTripToastLeading({
  place,
  searchResult,
  flightItem,
  isFlightItinerary,
  photoUrl,
  eventId,
  displayName,
}: {
  place?: Place
  searchResult?: WebSearchResultItem["searchResult"]
  flightItem?: FlightItineraryItem
  isFlightItinerary: boolean
  photoUrl?: string
  eventId?: ItineraryEvent["id"]
  displayName: string
}) {
  return (
    <span
      className={cn(
        "relative mr-1 flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg",
        !photoUrl &&
          !(isFlightItinerary && flightItem?.airlines?.length) &&
          eventColorPair(displayName),
        !eventId && "opacity-30"
      )}
    >
      {photoUrl ? (
        <Photo
          src={photoUrl}
          width="sm"
          className="absolute inset-0 size-full"
          alt=""
        />
      ) : searchResult ? (
        <NamedIcon name={getSearchResultIconName(searchResult)} size={16} />
      ) : place ? (
        <EntityIcon place={place} size={16} />
      ) : isFlightItinerary && flightItem?.airlines?.length ? (
        <AirlinesImg
          airlines={flightItem.airlines}
          logoSize={24}
          iconSize={20}
        />
      ) : isFlightItinerary ? (
        <EntityIcon type="flight" size={16} />
      ) : (
        <EntityIcon type="generic" size={16} />
      )}
    </span>
  )
}

function addToTripToastDescription({
  displayName,
  trip,
  eventId,
}: {
  displayName: string
  trip: TripSummary
  eventId?: ItineraryEvent["id"]
}) {
  if (eventId) {
    return (
      <>
        <span className="no-translate">{displayName}</span> added to{" "}
        <Link
          href={links.trip(trip.ref)}
          className="no-translate font-semibold hover:underline"
          onClick={() => toastManager.close()}
        >
          {trip.name}
        </Link>
      </>
    )
  }

  return (
    <>
      <span className="no-translate">{displayName}</span> removed from{" "}
      <span className="no-translate">{trip.name}</span>
    </>
  )
}

export const showAddToTripToast = createAsyncThunk<
  void,
  {
    item: TripAddableItem
    trip: TripSummary
    eventId?: ItineraryEvent["id"]
    allowChange?: boolean
    router?: NextRouter
    displayName?: string
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "tripThunks/showAddToTripToast",
  async (
    {
      item,
      eventId,
      trip,
      allowChange,
      router,
      displayName: displayNameOverride,
    },
    { dispatch, getState }
  ) => {
    const currentTripRef = selectTripRef(getState())
    const hideCreateTrip = selectHideCreateTrip(getState())

    const isFlightItinerary = item.kind === "flightItinerary"
    const place = isPlaceBasedItem(item) ? item.place : undefined
    const searchResult =
      item.kind === "webSearchResult" ? item.searchResult : undefined
    const flightItem = isFlightItinerary ? item : undefined
    const displayName = displayNameOverride ?? addToTripItemDisplayName(item)

    const photoUrl =
      place?.photos?.[0]?.url || (searchResult && searchResult.photo_url)

    const openTrip = async () => {
      toastManager.close()
      const isChat =
        router && (selectIsEmbeddedChatView(getState()) || isChatPage(router))
      const isExplore =
        router &&
        (selectIsEmbeddedExploreView(getState()) || isExplorePage(router))

      if (
        isExplore ||
        (currentTripRef !== trip.ref && isChat && !hideCreateTrip)
      ) {
        await router?.push(links.trip(trip.ref))
        void dispatch(openTripSheet({ eventId }))
      } else {
        void dispatch(openTripSheet({ tripRef: trip.ref, eventId }))
      }
    }

    const changeTripItem = async () => {
      toastManager.close()
      if (isFlightItinerary && eventId) {
        const flightApi = new FlightApi(buildClientAxios())
        await Promise.all(
          item.flightItineraryIds.map((flightItineraryId) =>
            flightApi.deleteFlightFromTrip(trip.ref, flightItineraryId)
          )
        )
        dispatch(
          itineraryApi.util.invalidateTags([
            { type: "Itinerary", id: trip.ref },
          ])
        )
        void dispatch(
          openAddToTripDialog({
            item,
            trackingContext: "AddToTripToast",
          })
        )
      } else if (eventId && place) {
        try {
          await dispatch(
            removePlaceFromTrip({
              place,
              trip,
              showToast: false,
            })
          ).unwrap()
          void dispatch(
            openAddToTripDialog({
              item,
              trackingContext: "AddToTripToast",
            })
          )
        } catch {
          // removePlaceFromTrip presents the error before rejecting.
        }
      } else {
        void dispatch(
          openAddToTripDialog({
            item,
            trackingContext: "AddToTripToast",
          })
        )
      }
    }

    const actionProps =
      allowChange && (place || isFlightItinerary) && eventId
        ? {
            children: "Change",
            onClick: () => {
              void changeTripItem()
            },
          }
        : eventId
          ? {
              children: "View trip",
              onClick: () => {
                void openTrip()
              },
            }
          : undefined

    toastManager.add({
      description: addToTripToastDescription({
        displayName,
        trip,
        eventId,
      }),
      timeout: TOAST_WITH_ACTION_DURATION,
      data: {
        leading: addToTripToastLeading({
          place,
          searchResult,
          flightItem,
          isFlightItinerary,
          photoUrl,
          eventId,
          displayName,
        }),
      },
      actionProps,
    })
  }
)

export const removePlaceFromTrip = createAsyncThunk<
  void,
  {
    place: Place
    trip: TripSummary
    showToast?: boolean
  },
  { dispatch: AppDispatch }
>(
  "tripThunks/removePlaceFromTrip",
  async ({ place, trip, showToast = true }, { dispatch }) => {
    await dispatch(
      removeEventsWithPlace({ placeRefs: place.entity_ref, tripRef: trip.ref })
    ).unwrap()

    if (showToast) {
      void dispatch(showAddToTripToast({ item: createPlaceItem(place), trip }))
    }
  }
)
